import { defer, switchMap, timer } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  effect,
  inject,
  Injector,
  Input,
  input,
  Output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';

import { SidenavRef } from '@shared/modules/sidenav/models/sidenav.models';
import { SidenavMenu } from '@shared/modules/sidenav/directives/sidenav-menu.directive';
import { SidenavTitle } from '@shared/modules/sidenav/directives/sidenav-title.directive';
import { SidenavContent } from '@shared/modules/sidenav/directives/sidenav-content.directive';
import { SidenavDirective } from '@shared/modules/sidenav/directives/sidenav.directive';
import { SidenavDiscardDialog } from '@shared/modules/sidenav/directives/sidenav-discard-dialog.directive';
import { SidenavButtonPrimary } from '@shared/modules/sidenav/directives/sidenav-button-primary.directive';
import { SidenavButtonSecondary } from '@shared/modules/sidenav/directives/sidenav-button-secondary.directive';
import { SidenavHeaderDirective } from '@shared/modules/sidenav/directives/sidenav-header.directive';
import { SidenavBannerDirective } from '@shared/modules/sidenav/directives/sidenav-banner.directive';
import { InlineDialog, InlineDialogTriggerFor } from '@shared/components/inline-dialog/inline-dialog.component';

@Component({
  selector: 'zef-sidenav',
  exportAs: 'zefSidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  animations: [
    trigger('toggle', [
      state('*', style({ transform: 'translateX(0)' })),
      transition('left => void', animate('200ms ease-in-out', style({ transform: 'translateX(-100%)' }))),
      transition('void => left', [style({ transform: 'translateX(-100%)' }), animate('200ms ease-in-out')]),
      transition('right => void', animate('200ms ease-in-out', style({ transform: 'translateX(100%)' }))),
      transition('void => right', [style({ transform: 'translateX(100%)' }), animate('200ms ease-in-out')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class Sidenav {
  private injector = inject(Injector);

  private sidenavRef = inject(SidenavRef, { optional: true });

  @Input()
  set confirmClose(confirmClose: boolean) {
    this._confirmClose = confirmClose;

    this.sn.confirmClose = this.confirmClose
      ? defer(() => {
          if (!this.confirmMenu.open) {
            this.confirmDialogTrigger.openDialog();
          }

          return timer(1).pipe(
            switchMap(() => this.confirmMenu.confirm.pipe(takeUntil(this.confirmMenu.close))),
            map(() => true),
          );
        })
      : undefined;
  }

  get confirmClose(): boolean {
    return this._confirmClose;
  }

  private _confirmClose = false;

  @Input()
  loading?: boolean = false;

  @Input()
  noHeader?: boolean;

  @Input()
  withScroll: boolean = true;

  readonly fullScreenToggle = input(false, { transform: booleanAttribute });

  readonly manualFullScreenToggle = input(false, { transform: booleanAttribute });

  readonly showFullscreenToggle = computed(() => this.fullScreenToggle() || this.manualFullScreenToggle());

  readonly fullscreen = signal(false);

  @ContentChild(SidenavContent)
  content?: SidenavContent;

  @ContentChild(SidenavTitle)
  _title?: SidenavTitle;

  @ContentChild(SidenavMenu)
  _menu?: SidenavMenu;

  @ContentChild(SidenavDiscardDialog)
  _discardDialog?: SidenavDiscardDialog;

  @ContentChild(SidenavButtonPrimary)
  _buttonPrimary?: SidenavButtonPrimary;

  @ContentChild(SidenavButtonSecondary)
  _buttonSecondary?: SidenavButtonSecondary;

  @ContentChild(SidenavHeaderDirective)
  header?: SidenavHeaderDirective;

  @ContentChild(SidenavBannerDirective)
  banner?: SidenavBannerDirective;

  @ViewChild('trigger', { read: InlineDialogTriggerFor })
  confirmDialogTrigger?: InlineDialogTriggerFor;

  @ViewChild('confirmMenu')
  confirmMenu?: InlineDialog;

  @Output()
  readonly close = this.sidenavRef?.sidenav.close;

  @Output()
  readonly afterClosed = this.sidenavRef?.sidenav.afterClosed;

  get sn(): SidenavDirective {
    return this.injector.get(SidenavDirective, null, { optional: true });
  }

  get title(): SidenavTitle {
    return this._title || this.header?.title;
  }

  get menu(): SidenavMenu {
    return this._menu || this.header?.menu;
  }

  get buttonPrimary(): SidenavButtonPrimary {
    return this._buttonPrimary || this.header?.buttonPrimary;
  }

  get buttonSecondary(): SidenavButtonSecondary {
    return this._buttonSecondary || this.header?.buttonSecondary;
  }

  get discardDialog(): SidenavDiscardDialog {
    return this._discardDialog || this.header?.discardDialog;
  }

  constructor() {
    effect(() => {
      const fullscreen = this.fullscreen();
      const manualFullScreenToggle = untracked(() => this.manualFullScreenToggle());

      if (!manualFullScreenToggle) {
        this.toggleFullscreenSize(fullscreen, true);
      }
    });
  }

  toggleFullscreenSize(fullscreen: boolean, animate?: boolean) {
    const width = fullscreen ? '100vw' : this.sn?.config.width;

    if (animate) {
      this.sn.dialogReference.addPanelClass('animate-width');
      setTimeout(() => this.sn?.dialogReference?.updateSize(width, this.sn?.config.height));
      setTimeout(() => this.sn.dialogReference.removePanelClass('animate-width'), 400);
    } else {
      this.sn?.dialogReference?.updateSize(width, this.sn?.config.height);
    }
  }

  closeSidenav(): void {
    if (this.sn) {
      this.sn.resetClosing();
      this.sn.closeSidenav();
    }
  }
}
